import { addMonths, startOfMonth } from "date-fns";
import type { Identifier } from "ra-core";

import { dayKey, toLocalDate } from "../../mobile/mobileModel";
import { computeMonthGoal } from "../../layout/desk/shellModel";
import {
  isOpenDeal,
  type HomeDeal,
  type HomeInvoice,
  type HomeMilestone,
} from "./homeModel";
import type { Subscription, TimeEntry } from "./homeTypes";

/**
 * Pure data shaping of the «Pulso del negocio» home (view B). Revenue uses
 * invoice amounts WITHOUT VAT (what the business earns); collection uses the
 * VAT-included total (see homeModel.collectBuckets).
 */

const amountOf = (value: number | null | undefined): number =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

const monthKey = (value: string | Date): string => dayKey(value).slice(0, 7);

export interface PipelineSummary {
  amount: number;
  count: number;
}

/** Open pipeline: every open deal (see isOpenDeal), whatever its closing date. */
export const openPipeline = (
  deals: readonly HomeDeal[],
  closedStages: readonly string[],
): PipelineSummary =>
  deals
    .filter((deal) => isOpenDeal(deal, closedStages))
    .reduce(
      (acc, deal) => ({
        amount: acc.amount + amountOf(deal.amount),
        count: acc.count + 1,
      }),
      { amount: 0, count: 0 },
    );

export interface WonThisMonth {
  /** Won deals whose expected closing date falls this month. */
  amount: number;
  /** Every non-lost deal expected to close this month («cierres previstos»). */
  expected: number;
  expectedCount: number;
}

/**
 * APPROXIMATION: Avante stores no real «won at» date, so a won deal counts
 * for the month of its expected closing date (same rule as the sidebar card).
 */
export const wonThisMonth = (
  deals: readonly HomeDeal[],
  wonStages: string[],
  now: Date,
): WonThisMonth => {
  const goal = computeMonthGoal(
    deals.map((deal) => ({
      amount: amountOf(deal.amount),
      stage: deal.stage,
      expected_closing_date: deal.expected_closing_date,
      archived_at: deal.archived_at,
    })),
    wonStages,
    now,
  );
  return {
    amount: goal.won,
    expected: goal.expected,
    expectedCount: goal.count,
  };
};

export interface MonthRevenue {
  /** "yyyy-MM". */
  key: string;
  /** First day of the month (local). */
  month: Date;
  /** Issued this month (not drafts), without VAT. */
  invoiced: number;
  invoicedCount: number;
  /** Paid this month (by paid_at), without VAT. */
  collected: number;
}

/** Invoiced vs collected for the last `months` months, oldest first. */
export const revenueByMonth = (
  invoices: readonly HomeInvoice[],
  now: Date,
  months = 6,
): MonthRevenue[] => {
  const first = startOfMonth(addMonths(now, -(months - 1)));
  const rows = Array.from({ length: months }, (_, index) => {
    const month = addMonths(first, index);
    return {
      key: monthKey(month),
      month,
      invoiced: 0,
      invoicedCount: 0,
      collected: 0,
    };
  });
  const byKey = new Map(rows.map((row) => [row.key, row]));
  for (const invoice of invoices) {
    if (invoice.status === "draft") continue;
    const net = amountOf(invoice.amount);
    const issued = invoice.issue_date
      ? byKey.get(monthKey(invoice.issue_date))
      : undefined;
    if (issued) {
      issued.invoiced += net;
      issued.invoicedCount += 1;
    }
    const paid =
      invoice.status === "paid" && invoice.paid_at
        ? byKey.get(monthKey(invoice.paid_at))
        : undefined;
    if (paid) paid.collected += net;
  }
  return rows;
};

export interface FunnelStage {
  stage: string;
  label: string;
  amount: number;
  count: number;
}

/** Open deals per open stage, in the configured stage order. */
export const funnelByStage = (
  deals: readonly HomeDeal[],
  stages: readonly { value: string; label: string }[],
  closedStages: readonly string[],
): FunnelStage[] => {
  const open = deals.filter((deal) => isOpenDeal(deal, closedStages));
  return stages
    .filter((stage) => isOpenDeal({ stage: stage.value }, closedStages))
    .map((stage) => {
      const inStage = open.filter((deal) => deal.stage === stage.value);
      return {
        stage: stage.value,
        label: stage.label,
        count: inStage.length,
        amount: inStage.reduce((sum, deal) => sum + amountOf(deal.amount), 0),
      };
    });
};

/** Open deals whose expected closing date is already past, most at stake first. */
export const overdueClosings = <T extends HomeDeal>(
  deals: readonly T[],
  closedStages: readonly string[],
  now: Date,
): T[] => {
  const today = dayKey(now);
  return deals
    .filter(
      (deal) =>
        isOpenDeal(deal, closedStages) &&
        !!deal.expected_closing_date &&
        dayKey(deal.expected_closing_date) < today,
    )
    .sort((left, right) => amountOf(right.amount) - amountOf(left.amount));
};

export interface DeliveryRow {
  dealId: Identifier;
  name: string;
  progress: number;
  milestonesDone: number;
  milestones: number;
  nbOverdue: number;
  nextDue: string | null;
  nextTitle: string | null;
}

/**
 * «Proyectos en entrega»: deliveries not finished yet (progress < 100) with
 * their deal name and next pending milestone, latest-risk first (overdue
 * milestones, then the nearest next due date).
 */
export const deliveryRows = (
  deliveries: readonly {
    id: Identifier;
    progress: number;
    nb_milestones: number;
    nb_milestones_done: number;
    nb_overdue: number;
    next_due_date?: string | null;
  }[],
  deals: readonly Pick<HomeDeal, "id" | "name">[],
  milestones: readonly HomeMilestone[],
): DeliveryRow[] =>
  deliveries
    .filter((delivery) => delivery.progress < 100 && delivery.nb_milestones > 0)
    .map((delivery) => {
      const next = milestones
        .filter(
          (milestone) =>
            String(milestone.deal_id) === String(delivery.id) &&
            milestone.status !== "done",
        )
        .sort((left, right) =>
          String(left.due_date ?? "9999").localeCompare(
            String(right.due_date ?? "9999"),
          ),
        )[0];
      return {
        dealId: delivery.id,
        name:
          deals.find((deal) => String(deal.id) === String(delivery.id))?.name ??
          "",
        progress: Math.max(0, Math.min(100, Math.round(delivery.progress))),
        milestonesDone: delivery.nb_milestones_done,
        milestones: delivery.nb_milestones,
        nbOverdue: delivery.nb_overdue,
        nextDue: next?.due_date ?? delivery.next_due_date ?? null,
        nextTitle: next?.title ?? null,
      };
    })
    .sort(
      (left, right) =>
        right.nbOverdue - left.nbOverdue ||
        String(left.nextDue ?? "9999").localeCompare(
          String(right.nextDue ?? "9999"),
        ),
    );

export interface HoursSummary {
  total: number;
  billable: number;
  /** 0..1, 0 when no hours. */
  billableRatio: number;
  /** Hours per deal, most first; `dealId` null = not linked to a deal. */
  byDeal: { dealId: Identifier | null; hours: number }[];
}

/** Hours logged this calendar month. */
export const hoursThisMonth = (
  entries: readonly Pick<
    TimeEntry,
    "date" | "hours" | "billable" | "deal_id"
  >[],
  now: Date,
): HoursSummary => {
  const month = monthKey(now);
  const inMonth = entries.filter((entry) => monthKey(entry.date) === month);
  const total = inMonth.reduce((sum, entry) => sum + amountOf(entry.hours), 0);
  const billable = inMonth
    .filter((entry) => entry.billable)
    .reduce((sum, entry) => sum + amountOf(entry.hours), 0);
  const perDeal = new Map<
    string,
    { dealId: Identifier | null; hours: number }
  >();
  for (const entry of inMonth) {
    const key = entry.deal_id == null ? "none" : String(entry.deal_id);
    const row = perDeal.get(key) ?? { dealId: entry.deal_id ?? null, hours: 0 };
    perDeal.set(key, { ...row, hours: row.hours + amountOf(entry.hours) });
  }
  return {
    total,
    billable,
    billableRatio: total > 0 ? billable / total : 0,
    byDeal: [...perDeal.values()].sort(
      (left, right) => right.hours - left.hours,
    ),
  };
};

export interface FixedCosts {
  /** Active recurring subscriptions per month (yearly ones / 12). */
  monthly: number;
  yearly: number;
  count: number;
  /** Next renewals from today on, soonest first. */
  upcoming: Pick<
    Subscription,
    "id" | "name" | "amount" | "billing_period" | "next_renewal" | "category"
  >[];
}

export const fixedCosts = (
  subscriptions: readonly Subscription[],
  now: Date,
  upcomingCount = 3,
): FixedCosts => {
  const today = dayKey(now);
  const active = subscriptions.filter(
    (subscription) =>
      subscription.status === "active" &&
      subscription.billing_period !== "one_off",
  );
  const monthly = active.reduce(
    (sum, subscription) =>
      sum +
      (subscription.billing_period === "yearly"
        ? amountOf(subscription.amount) / 12
        : amountOf(subscription.amount)),
    0,
  );
  return {
    monthly,
    yearly: monthly * 12,
    count: active.length,
    upcoming: active
      .filter(
        (subscription) =>
          !!subscription.next_renewal &&
          dayKey(subscription.next_renewal) >= today,
      )
      .sort((left, right) =>
        String(left.next_renewal).localeCompare(String(right.next_renewal)),
      )
      .slice(0, upcomingCount),
  };
};

/** Invoices issued this calendar month (not drafts), without VAT. */
export const invoicedThisMonth = (
  invoices: readonly HomeInvoice[],
  now: Date,
): { amount: number; count: number } => {
  const month = monthKey(now);
  return invoices
    .filter(
      (invoice) =>
        invoice.status !== "draft" &&
        !!invoice.issue_date &&
        monthKey(toLocalDate(invoice.issue_date)) === month,
    )
    .reduce(
      (acc, invoice) => ({
        amount: acc.amount + amountOf(invoice.amount),
        count: acc.count + 1,
      }),
      { amount: 0, count: 0 },
    );
};
